import React, { useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { createPortal } from 'react-dom'
import { useDelayUnmount, classNames, useLockBodyScroll, useLockFocus } from './utils'
import { colors, widths } from '../../styles/variables'

interface Props {
	backgroundCanClose?: boolean
	isOpen: boolean
	toggle: () => void
	labelledBy?: string
}

/** KeyboardEvent.which value for Escape (Esc) key */
const ESCAPE_KEYCODE = 27

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.6;
  }
`

const fadeOut = keyframes`
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 0;
  }
`

const StyledModal = styled.div`
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		z-index: 5;
		width: 100vw;
		height: 100vh;
		background-color: ${colors.gray.dark};
		opacity: 0;

		/* Fade transition */
		animation: ${fadeIn} var(--animation-length) forwards;

		&.is-closing {
			animation: ${fadeOut} var(--animation-length) forwards;
		}
	}

	.modal {
		position: fixed;
		top: 0;
		left: 0;
		z-index: 10;
		display: none;
		width: 100%;
		height: 100%;
		overflow-x: hidden;
		overflow-y: auto;
		/* Prevent Chrome on Windows from adding a focus outline. For details, see */
		/* https://github.com/twbs/bootstrap/pull/10951. */
		outline: 0;
		/* We deliberately don't use '-webkit-overflow-scrolling: touch;' due to a */
		/* gnarly iOS Safari bug: https://bugs.webkit.org/show_bug.cgi?id=158342 */
		/* See also https://github.com/twbs/bootstrap/issues/17695 */

		&.is-open .modal-dialog {
			opacity: 1;
			transform: translateY(0px);
		}

		&.is-closing .modal-dialog {
			opacity: 0;
			transform: translateY(-50px);
		}
	}

	.modal-dialog {
		margin: 3rem auto;
		max-width: ${widths.lg}px;
		padding: var(--spacing);
		opacity: 0;

		transition: opacity var(--animation-length) ease-out, transform var(--animation-length) ease-out;
		transform: translateY(-50px);
	}
`

const Modal: React.FC<Props> = props => {
	const animationTime = 300
	const ref = useRef<HTMLDivElement>(null)
	const isVisible = useDelayUnmount(props.isOpen, animationTime)

	// Close modal when escape is pressed.
	const escapeEvent = (event: KeyboardEvent) => {
		if (event.which === ESCAPE_KEYCODE) {
			event.preventDefault()
			props.toggle()
		}
	}

	// Lock focus inside HTML element.
	useLockFocus(props.isOpen, ref)

	// Keep HTML body from scrolling.
	useLockBodyScroll(props.isOpen || isVisible)

	useEffect(() => {
		const element = ref.current!

		if (props.isOpen) {
			element.classList.add('is-open')
			element.addEventListener('keydown', escapeEvent)

			// Cleanup
			return () => {
				element.removeEventListener('keydown', escapeEvent)
			}
		}
	}, [props.isOpen])

	if (typeof document === 'undefined') {
		return <></>
	}

	return createPortal(
		<StyledModal style={{ '--animation-length': `${animationTime}ms` } as any}>
			{/* Conditionally render overlay */}
			{isVisible && <div className={classNames('modal-overlay', !props.isOpen && 'is-closing')} />}

			<div
				ref={ref}
				role="dialog"
				tabIndex={-1}
				style={{ display: props.isOpen || isVisible ? 'block' : 'none' }}
				className={classNames('modal', !props.isOpen && isVisible && 'is-closing')}
				onClick={event => {
					props.backgroundCanClose && props.toggle()
				}}
				aria-labelledby={props.labelledBy}
				aria-modal={props.isOpen}
				aria-hidden={!props.isOpen}
			>
				<div className="modal-dialog" role="document">
					{props.children}
				</div>
			</div>
		</StyledModal>,
		document.body
	)
}

export default Modal
